import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { DateRange } from 'react-day-picker'
import { DatePickerMode } from '../types'
import { PRESET_LABELS } from '../constants'
import { getPresetRanges, checkPresetMatch } from '../utils'

export function useDatePicker(
  value: Date | DateRange | undefined,
  onChange: (date: Date | DateRange | undefined) => void,
  mode: DatePickerMode,
  placeholder: string
) {
  const [open, setOpen] = useState(false)
  const [tempDate, setTempDate] = useState<Date | undefined>(mode === DatePickerMode.Single ? (value as Date) : undefined)
  const [tempRange, setTempRange] = useState<DateRange | undefined>(mode === DatePickerMode.Range ? (value as DateRange) : undefined)
  const [presetValue, setPresetValue] = useState<string>('')
  const lastCompleteRangeRef = useRef<DateRange | undefined>(undefined)

  const presets = useMemo(() => getPresetRanges(), [])

  const handlePresetChange = useCallback(
    (preset: string) => {
      const selectedPreset = presets[preset as keyof typeof presets]
      if (selectedPreset) {
        setTempRange(selectedPreset)
        setPresetValue(preset)
        lastCompleteRangeRef.current = selectedPreset
      }
    },
    [presets]
  )

  const matchPreset = useCallback(
    (range: DateRange | undefined) => checkPresetMatch(range, presets),
    [presets]
  )

  const getPresetDisplayValue = useCallback((): string => {
    if (!tempRange?.from || !tempRange?.to) return ''
    if (matchPreset(tempRange)) return ''
    return 'custom'
  }, [tempRange, matchPreset])

  useEffect(() => {
    if (!open) return
    if (mode === DatePickerMode.Single) {
      setTempDate(value as Date)
    } else {
      setTempRange(value as DateRange)
      const range = value as DateRange
      if (range && typeof range === 'object' && range.from && range.to) {
        lastCompleteRangeRef.current = range
        const matched = matchPreset(range)
        setPresetValue(matched || 'custom')
      } else {
        setPresetValue('')
      }
    }
  }, [open, value, mode, matchPreset])

  const handleSelectSingle = useCallback((date: Date | undefined) => {
    setTempDate(date)
  }, [])

  const handleSelectRange = useCallback(
    (range: DateRange | undefined) => {
      if (!range) {
        setTempRange(undefined)
        lastCompleteRangeRef.current = undefined
        setPresetValue('')
        return
      }
      setPresetValue('')
      const last = lastCompleteRangeRef.current
      if (last?.from && last?.to) {
        const lastFromTime = last.from.getTime()
        const lastToTime = last.to.getTime()
        let clickedDate: Date | undefined
        if (range.from && range.to) {
          const newFromTime = range.from.getTime()
          const newToTime = range.to.getTime()
          if (newFromTime === lastFromTime && newToTime !== lastToTime) clickedDate = range.to
          else if (newFromTime !== lastFromTime) clickedDate = range.from
          else clickedDate = range.to
        } else if (range.from) clickedDate = range.from
        else if (range.to) clickedDate = range.to
        if (clickedDate) {
          setTempRange({ from: clickedDate, to: undefined })
          lastCompleteRangeRef.current = undefined
          return
        }
      }
      setTempRange(range)
      if (range.from && range.to) {
        lastCompleteRangeRef.current = range
        const matched = matchPreset(range)
        setPresetValue(matched || 'custom')
      } else {
        setPresetValue('')
      }
    },
    [matchPreset]
  )

  const handleConfirm = useCallback(() => {
    if (mode === DatePickerMode.Single) {
      onChange(tempDate)
    } else {
      onChange(tempRange)
    }
    setOpen(false)
  }, [mode, tempDate, tempRange, onChange])

  const formatDisplayValue = useCallback((): string => {
    if (!value) return placeholder
    if (mode === DatePickerMode.Single) {
      const date = value as Date
      return format(date, 'dd/MM/yy', { locale: ptBR })
    }
    const range = value as DateRange
    if (range?.from && range?.to) {
      const matched = matchPreset(range)
      if (matched) return PRESET_LABELS[matched] ?? placeholder
      return `${format(range.from, 'dd/MM/yy', { locale: ptBR })} - ${format(range.to, 'dd/MM/yy', { locale: ptBR })}`
    }
    if (range?.from) return format(range.from, 'dd/MM/yy', { locale: ptBR })
    return placeholder
  }, [value, mode, placeholder, matchPreset])

  const presetSelectValue = presetValue || getPresetDisplayValue() || undefined
  const presetSelectLabel =
    presetValue in PRESET_LABELS
      ? PRESET_LABELS[presetValue as keyof typeof PRESET_LABELS]
      : presetValue === 'custom' || getPresetDisplayValue() === 'custom'
        ? PRESET_LABELS.custom
        : PRESET_LABELS['']

  return {
    open,
    setOpen,
    tempDate,
    tempRange,
    presetValue,
    presetSelectValue,
    presetSelectLabel,
    handlePresetChange,
    handleSelectSingle,
    handleSelectRange,
    handleConfirm,
    formatDisplayValue,
  }
}
